import fs from 'fs';
import axios from '@utils/axios';

// project/getPublic is shared by many projects and takes ~20s for large tours, and the API does not
// cache it. This keeps the raw response per URL in server memory (and on disk, so a restart does not
// start cold) and serves it stale-while-revalidate. Raw JSON text is stored, not the parsed object,
// because the redux store freezes whatever it is given.

type Entry = { text: string; at: number };

const TTL_MS = Number(process.env.PUBLIC_CACHE_TTL_SECONDS || 300) * 1000;
const MAX_STALE_MS = 7 * 24 * 3600 * 1000;
const KEEP_FRESH_MS = 6 * 3600 * 1000;
const cacheDir = () =>
  process.env.PUBLIC_CACHE_DIR || `${process.cwd()}/.next/cache/public-project`;

type State = {
  entries: Map<string, Entry>;
  inflight: Map<string, Promise<Entry>>;
  lastUsed: Map<string, number>;
  timer?: NodeJS.Timeout;
};

const globalRef = globalThis as unknown as { __publicProjectCache?: State };
const state: State = (globalRef.__publicProjectCache ||= {
  entries: new Map(),
  inflight: new Map(),
  lastUsed: new Map(),
});

const fileOf = (key: string) =>
  `${cacheDir()}/${key.replace(/[^a-zA-Z0-9]+/g, '_').slice(0, 150)}.json`;

const readDisk = (key: string): Entry | undefined => {
  try {
    const file = fileOf(key);
    const text = fs.readFileSync(file, 'utf8');
    return { text, at: fs.statSync(file).mtimeMs };
  } catch {
    return undefined;
  }
};

const writeDisk = (key: string, text: string) => {
  try {
    fs.mkdirSync(cacheDir(), { recursive: true });
    fs.writeFileSync(fileOf(key), text);
  } catch {
    // disk is optional (read-only container fs)
  }
};

const fetchFresh = (key: string): Promise<Entry> => {
  const running = state.inflight.get(key);
  if (running) return running;

  const job = axios
    .get(key, { responseType: 'text', transformResponse: r => r })
    .then(response => {
      const text = response.data as string;
      if (!JSON.parse(text)?.data) throw new Error('empty project response');
      const entry = { text, at: Date.now() };
      state.entries.set(key, entry);
      writeDisk(key, text);
      return entry;
    })
    .finally(() => state.inflight.delete(key));

  state.inflight.set(key, job);
  return job;
};

const startKeepFresh = () => {
  if (state.timer) return;
  state.timer = setInterval(() => {
    const now = Date.now();
    state.lastUsed.forEach((usedAt, key) => {
      if (now - usedAt > KEEP_FRESH_MS) {
        state.lastUsed.delete(key);
        return;
      }
      const entry = state.entries.get(key);
      if (!entry || now - entry.at >= TTL_MS)
        fetchFresh(key).catch(() => undefined);
    });
  }, Math.max(TTL_MS / 2, 30000));
  state.timer.unref?.();
};

/** Returns the parsed body of a getPublic-style GET, shaped like an axios response. */
export const getCachedPublicProject = async (key: string) => {
  state.lastUsed.set(key, Date.now());
  startKeepFresh();

  let entry = state.entries.get(key);
  if (!entry) {
    entry = readDisk(key);
    if (entry) state.entries.set(key, entry);
  }

  const age = entry ? Date.now() - entry.at : Infinity;
  if (entry && age < MAX_STALE_MS) {
    if (age >= TTL_MS) fetchFresh(key).catch(() => undefined);
    return { data: JSON.parse(entry.text) };
  }

  const fresh = await fetchFresh(key);
  return { data: JSON.parse(fresh.text) };
};

export const warmPublicProject = (slug: string) => {
  const key = `project/getPublic?slug=${slug}`;
  state.lastUsed.set(key, Date.now());
  startKeepFresh();
  if (!state.entries.has(key)) {
    const disk = readDisk(key);
    if (disk) state.entries.set(key, disk);
  }
  const entry = state.entries.get(key);
  if (!entry || Date.now() - entry.at >= TTL_MS)
    fetchFresh(key).catch(() => undefined);
};

/** Forces a refetch and waits for it; the previous copy keeps being served meanwhile. */
export const refreshPublicProject = async (slug: string) => {
  const key = `project/getPublic?slug=${slug}`;
  state.lastUsed.set(key, Date.now());
  startKeepFresh();
  const entry = await fetchFresh(key);
  return { at: entry.at, bytes: entry.text.length };
};
